import { useRef } from "react";
import { motion, useReducedMotion, useScroll, useTransform } from "framer-motion";
import { scrollToId } from "../lib/scroll";

const EASE = [0.22, 1, 0.36, 1];

const HERO_IMG = "/images/hero-mats.jpg";

export default function Hero() {
    const reduce = useReducedMotion();
    const imgWrap = useRef(null);
    const { scrollYProgress } = useScroll({ target: imgWrap, offset: ["start end", "end start"] });
    const parallaxY = useTransform(scrollYProgress, [0, 1], reduce ? [0, 0] : [-20, 32]);

    const line = (children, i) => (
        <span className="block overflow-hidden pb-1.5">
            <motion.span
                className="block"
                initial={reduce ? { opacity: 0 } : { y: "112%" }}
                animate={reduce ? { opacity: 1 } : { y: "0%" }}
                transition={{ duration: 0.9, ease: EASE, delay: 0.15 + i * 0.14 }}
            >
                {children}
            </motion.span>
        </span>
    );

    const fade = (children, delay) => (
        <motion.div
            initial={{ opacity: 0, y: reduce ? 0 : 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: EASE, delay }}
        >
            {children}
        </motion.div>
    );

    return (
        <section id="top" className="relative overflow-hidden pb-12 pt-6 sm:pb-16 sm:pt-10 lg:pb-24 lg:pt-20">
            <div aria-hidden="true" className="pointer-events-none absolute -right-40 -top-40 h-[480px] w-[480px] rounded-full border-[3px] border-teal-600/15" />
            <div aria-hidden="true" className="pointer-events-none absolute right-24 top-40 h-24 w-24 rounded-full bg-brand-amber/20" />
            <div className="mx-auto grid max-w-7xl items-center gap-10 px-5 sm:px-8 lg:grid-cols-12 lg:gap-10">
                <div className="lg:col-span-6">
                    {fade(
                        <p className="mb-5 flex items-center gap-3 text-base font-bold uppercase tracking-[0.18em] text-teal-800 sm:mb-6">
                            <span className="h-[3px] w-10 rounded-full bg-brand-amber" aria-hidden="true" />
                            Small-group training · Arlington, Virginia
                        </p>,
                        0.05
                    )}
                    <h1 className="font-display text-[2.3rem] font-black leading-[1.05] tracking-tight text-ink sm:text-6xl lg:text-[4.25rem]">
                        {line(
                            <>
                                Ready to fall{" "}
                                <span className="relative inline-block text-teal-700">
                                    safely.
                                    <svg aria-hidden="true" viewBox="0 0 120 12" preserveAspectRatio="none" className="absolute -bottom-1 left-0 h-3 w-full">
                                        <path d="M3 9c30-6 80-6 114-3" stroke="#F59E0B" strokeWidth="5" fill="none" strokeLinecap="round" />
                                    </svg>
                                </span>
                            </>,
                            0
                        )}
                        {line("Get up confidently.", 1)}
                    </h1>
                    {fade(
                        <p className="mt-5 max-w-xl text-lg leading-relaxed text-ink-soft sm:mt-6">
                            Small-group training for active adults 55+ in Arlington, Virginia. Learn what to do
                            before, during and after a fall, at your own pace.
                        </p>,
                        0.45
                    )}
                    {fade(
                        <div className="mt-6 flex flex-col gap-3 sm:mt-9 sm:flex-row sm:items-center sm:gap-4">
                            <a
                                href="#join"
                                data-testid="hero-cta-interest"
                                onClick={(e) => {
                                    e.preventDefault();
                                    scrollToId("join");
                                }}
                                className="btn-primary text-lg sm:whitespace-nowrap"
                            >
                                Join the interest list
                            </a>
                            <a
                                href="#organizations"
                                data-testid="hero-cta-organizations"
                                onClick={(e) => {
                                    e.preventDefault();
                                    scrollToId("organizations");
                                }}
                                className="btn-outline text-lg sm:whitespace-nowrap"
                            >
                                Bring a workshop to your organization
                            </a>
                        </div>,
                        0.55
                    )}
                    {fade(
                        <p className="mt-5 flex items-center gap-2.5 text-base font-bold text-ink sm:mt-6" data-testid="hero-timeline">
                            <span className="relative flex h-2.5 w-2.5">
                                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-brand-amber opacity-60" />
                                <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-brand-amber" />
                            </span>
                            First workshops in early 2027
                        </p>,
                        0.65
                    )}
                </div>
                <div ref={imgWrap} className="lg:col-span-6">
                    <motion.div style={{ y: parallaxY }} className="relative">
                        <div
                            className="overflow-hidden rounded-[1.5rem] border border-clay shadow-[0_24px_60px_-24px_rgba(11,25,44,0.4)] sm:rounded-[2rem]"
                            data-testid="hero-image"
                        >
                            <img
                                src={HERO_IMG}
                                alt="Three panels of an older woman practicing a squat, a seated hold and a floor exercise on a padded mat while her coach watches"
                                className="aspect-[16/10] w-full object-cover"
                                loading="eager"
                                fetchPriority="high"
                            />
                        </div>
                        <div className="absolute -bottom-5 left-4 max-w-[250px] rounded-2xl border border-clay bg-cream px-5 py-4 sm:-bottom-7 sm:left-8">
                            <p className="font-display text-base font-extrabold text-ink">Practiced step by step</p>
                            <p className="text-base text-ink-soft">Gradual progress on padded mats</p>
                        </div>
                    </motion.div>
                </div>
            </div>
        </section>
    );
}